import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Blur,
  Container,
  FilterButton,
  FilterContainer,
  InfiniteScrollMarker,
  ItemContainer,
  ItemContainerGrid,
  LoaderMoreContainer,
  NameText,
  NameTextGrid,
  NameWrapper,
  NameWrapperGrid,
  RowGrid,
  StyledImage,
  StyledImageGrid,
  TelegramText,
  TelegramUsernameText,
} from './InfluencersList.styles';
import { useDispatch, useSelector } from 'react-redux';
import { selectGetInfluencersInfo, setInfluencer, setSelectedFilter } from '../../store/slices/influencer/slice';
import { getInfluencers } from '../../store/slices/influencer/asyncThunks';
import Spinner from '../Spinner/Spinner';
import { useNavigate } from 'react-router-dom';
import { INFLUENCER } from '../App/routes';
import InfluencersListSkeleton from '../skeleton/InfluencersListSkeleton/InfluencersListSkeleton';
import { ReactComponent as ChevronLeftIcon } from '../../assets/icons/chevron.svg';
import SearchInput from '../SearchInput/SearchInput';
import debounce from 'lodash.debounce';
import { triggerHaptic } from '../../utils/telegram';

const categories = [
  { name: '🔥 Trending', value: 'TRENDING' },
  { name: '💎 Most Popular', value: 'MOST_POPULAR' },
  { name: '💫 New', value: 'NEW' },
];

const InfluencersList = ({ gridView }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loadMoreRef = useRef();
  const [searchValue, setSearchValue] = useState('');

  const { page, data, maxReached, pending, fetchMorePending, selectedFilter } = useSelector(selectGetInfluencersInfo);

  const changeHandler = useCallback(
    text => {
      dispatch(getInfluencers({ page: 1, search: text, sortBy: selectedFilter }));
    },
    [dispatch, selectedFilter],
  );

  const debouncedChangeHandler = useMemo(() => debounce(changeHandler, 300), [changeHandler]);

  useEffect(() => {
    if (data.length) return;
    dispatch(getInfluencers({ page: 1, search: searchValue, sortBy: selectedFilter }));
    // eslint-disable-next-line
  }, [dispatch]);

  const handleIntersection = useCallback(
    entries => {
      const [entry] = entries;
      if (entry.isIntersecting && !pending && !fetchMorePending && !maxReached) {
        dispatch(
          getInfluencers({
            page: page + 1,
            search: searchValue,
            sortBy: selectedFilter,
          }),
        );
      }
    },
    [pending, maxReached, fetchMorePending, page, dispatch, searchValue, selectedFilter],
  );

  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersection, {
      root: null,
      threshold: 1.0,
    });
    const target = loadMoreRef.current;
    if (target) observer.observe(target);

    return () => {
      if (target) observer.unobserve(target);
    };
  }, [handleIntersection]);

  const onInfluencerClick = influencer => {
    dispatch(setInfluencer(influencer));
    triggerHaptic();
    navigate(`/${INFLUENCER.replace(':id', influencer.telegramUsername)}`);
  };

  return (
    <>
      <FilterContainer>
        <SearchInput
          searchValue={searchValue}
          onSearchChange={value => {
            setSearchValue(value);
            debouncedChangeHandler(value);
          }}
        />
        {categories.map(c => {
          return (
            <FilterButton
              $selected={c.value === selectedFilter}
              onClick={() => {
                triggerHaptic();
                dispatch(setSelectedFilter(c.value));
                dispatch(getInfluencers({ page: 1, search: searchValue, sortBy: c.value }));
              }}
              key={c.value}>
              {c.name}
            </FilterButton>
          );
        })}
      </FilterContainer>
      <Container $gridView={gridView}>
        {pending ? (
          <InfluencersListSkeleton gridView={gridView} />
        ) : (
          <>
            {data.map(influencer => {
              return !gridView ? (
                <InfluencerItemGrid
                  key={influencer._id}
                  name={influencer.name}
                  profilePhoto={influencer.profilePhoto}
                  telegramUsername={influencer.telegramUsername}
                  onClick={() => onInfluencerClick(influencer)}
                />
              ) : (
                <InfluencerItem
                  key={influencer._id}
                  name={influencer.name}
                  profilePhoto={influencer.profilePhoto}
                  onClick={() => onInfluencerClick(influencer)}
                />
              );
            })}
            {!!data.length && <InfiniteScrollMarker ref={loadMoreRef} />}
          </>
        )}
      </Container>
      {fetchMorePending && (
        <LoaderMoreContainer>
          <Spinner width={100} height={100} />
        </LoaderMoreContainer>
      )}
    </>
  );
};

const InfluencerItem = ({ name, profilePhoto, onClick }) => {
  return (
    <ItemContainer onClick={onClick}>
      <StyledImage src={profilePhoto} />
      <Blur />
      <NameWrapper>
        <NameText>{name}</NameText>
      </NameWrapper>
    </ItemContainer>
  );
};

const InfluencerItemGrid = ({ name, profilePhoto, onClick, telegramUsername }) => {
  return (
    <ItemContainerGrid onClick={onClick}>
      <StyledImageGrid src={profilePhoto} />
      <NameWrapperGrid>
        <NameTextGrid>{name}</NameTextGrid>
        <RowGrid>
          <TelegramText>Telegram</TelegramText>
          <ChevronLeftIcon />
          <TelegramUsernameText>@{telegramUsername}</TelegramUsernameText>
        </RowGrid>
      </NameWrapperGrid>
    </ItemContainerGrid>
  );
};

export default InfluencersList;
